import { createRouter, createWebHistory } from "vue-router"
import store from "../store"

import Portfolio from "@/views/dashboard/portfolio/Portfolio.vue"
import Dashboard from "@/views/dashboard/dashboard/Dashboard.vue"
import HomePage from "@/views/dashboard/homepage/HomePage.vue"
import CustomerInbox from "@/views/CustomerInbox/index.vue"

import Journeys from "@/views/dashboard/expansion/Journeys.vue"
import Prospecting from "@/views/dashboard/expansion/Prospecting.vue"

import ForecastsContainer from "@/views/dashboard/revenue/forecast/ForecastsContainer.vue"
import MainContainer from "@/views/dashboard/MainContainer.vue"

// Integrations
import IntegrationsList from "@/views/dashboard/admin/integrations/IntegrationsList.vue"
import IntegrationsMainContainer from "@/views/dashboard/admin/integrations/IntegrationsMainContainer.vue"
import ServiceCallback from "@/views/dashboard/admin/integrations/ServiceCallback.vue"

import AdminPage from "@/views/dashboard/settings/organization/AdminPage.vue"
import ApiKeys from "@/views/dashboard/settings/organization/ApiKeys.vue"

import SuggestedLists from "@/views/dashboard/settings/organization/SuggestedLists.vue"
import ForecastingPreferences from "@/views/dashboard/settings/organization/ForecastingPreferences.vue"
// import GlobalSettings from "@/views/dashboard/settings/organization/GlobalSettings.vue"
import GlobalVariables from "@/views/dashboard/settings/organization/GlobalVariables.vue"
import GrowthMatrices from "@/views/dashboard/settings/organization/GrowthMatrices.vue"
import Templates from "@/views/dashboard/settings/organization/Templates.vue"
// import RolesAndPermissions from "@/views/dashboard/settings/organization/RolesAndPermissions.vue"

import Settings from "@/views/dashboard/settings/profile/SettingsContainer.vue"
import AdminContainer from "@/views/dashboard/admin/AdminMainContainer.vue"
import Metrics from "@/views/dashboard/admin/Metrics.vue"
import Scores from "@/views/dashboard/admin/Scores.vue"
import NewScore from "@/views/dashboard/admin/NewScore.vue"

import Teams from "@/views/dashboard/admin/Teams.vue"
import NewTeam from "@/views/dashboard/admin/NewTeam.vue"

const Organization = () => import("@/views/dashboard/settings/organization/Organization.vue")
const Billing = () => import("@/views/dashboard/settings/organization/billing/Container.vue")

const Profile = () => import("@/views/dashboard/settings/profile/Profile.vue")
const AboutMe = () => import("@/views/dashboard/settings/profile/AboutMe.vue")
const PasswordReset = () => import("@/views/dashboard/settings/profile/ResetPassword.vue")
const Themes = () => import("@/views/dashboard/settings/profile/Themes.vue")
const MyActivities = () => import("@/views/dashboard/settings/profile/MyActivities.vue")

// Occasionally Used
const Login = () => import(/* webpackChunkName: "auth" */ "@/views/Login.vue")
const Signup = () => import(/* webpackChunkName: "auth" */ "@/views/Signup.vue")
const JoinTeam = () => import(/* webpackChunkName: "auth" */ "@/views/JoinTeam.vue")
const ResetPassword = () => import(/* webpackChunkName: "auth" */ "@/views/ResetPassword.vue")
const RecoverPassword = () => import(/* webpackChunkName: "auth" */ "@/views/RecoverPassword.vue")

const PrivacyPolicy = () => import(/* webpackChunkName: "standalone" */ "@/views/PrivacyPolicy.vue")
const TermsAndConditions = () => import(/* webpackChunkName: "standalone" */ "@/views/TermsAndConditions.vue")
const CookiesPolicy = () => import(/* webpackChunkName: "standalone" */ "@/views/CookiesPolicy.vue")
const PermissionRequiredPage = () => import(/* webpackChunkName: "standalone" */ "@/views/PermissionRequiredPage.vue")
const ProtectedResourcePage = () => import(/* webpackChunkName: "standalone" */ "@/views/ProtectedResourcePage.vue")
const PageNotFound = () => import(/* webpackChunkName: "standalone" */ "@/views/PageNotFound.vue")
const ExternalSurvey = () => import(/* webpackChunkName: "standalone" */ "@/views/ExternalSurvey.vue")

// Workspace
const WorkSpaceJoin = () => import(/* webpackChunkName: "workspace" */ "@/views/workspace/WorkSpaceJoin.vue")
const WorkSpaceLogin = () => import(/* webpackChunkName: "workspace" */ "@/views/workspace/WorkSpaceLogin.vue")
import WorkSpaceMain from "@/views/workspace/WorkSpaceMain.vue"
import WorkspacesList from "@/views/workspace/workspacesList"
import WorkspaceForm from "@/views/workspace/workspaceForm"
import WorkspaceItem from "@/views/workspace/WorkspaceItem.vue"

import ShareSpaceMain from "@/views/share-space/ShareSpaceMain.vue"
import ShareSpaceHome from "@/views/share-space/ShareSpaceHome.vue"
import ShareSpaceObjects from "@/views/share-space/ShareSpaceObjects.vue"
import ShareSpaceObject from "@/views/share-space/ShareSpaceObject.vue"

const ShareSpaceJoin = () => import(/* webpackChunkName: "share-space" */ "@/views/share-space/ShareSpaceJoin.vue")
const ShareSpaceLogin = () => import(/* webpackChunkName: "share-space" */ "@/views/share-space/ShareSpaceLogin.vue")

const OnboardingContainer = () => import(/* webpackChunkName: "onboarding" */ "@/views/onboarding/OnboardingContainer.vue")

// Often Used
import CompanyProfile from "@/views/CompanyProfile.vue"
import ObjectPageProfile from "@/views/ObjectPageProfile.vue"

import Playbooks from "@/views/dashboard/playbooks/Playbooks.vue"
import PlaybookEditor from "@/views/dashboard/playbooks/PlaybookEditor"
import PlaybookExecutions from "@/views/dashboard/playbooks/PlaybookExecutions.vue"

import Surveys from "@/views/dashboard/surveys/Surveys.vue"

import PlanPageContainer from "@/views/PlanPageContainer.vue"
import authService from "@/services/auth.service"
import { event as google_event } from "vue-gtag-next"

const routes = [
  {
    path: "/",
    component: MainContainer,
    name: "Home",
    children: [
      {
        path: "/admin",
        name: "AdminContainer",
        component: AdminContainer,
        children: [
          {
            path: "/admin/metrics/",
            meta: { page_reference: "metrics", page_scope_label: "Metrics" },
            name: "Metrics",
            component: Metrics
          },
          {
            path: "/admin/scores",
            meta: { page_reference: "scores", page_scope_label: "Scores" },
            name: "Scores",
            component: Scores
          },

          {
            path: "/admin/scores/edit/:sid",
            meta: { page_reference: "scoreedit", page_scope_label: "Edit Score" },
            name: "ScoreEdit",
            component: NewScore
          },
          {
            path: "/admin/scores/new-score",
            meta: { page_reference: "newscore", page_scope_label: "New Score" },
            name: "NewScore",
            component: NewScore
          },

          {
            path: "/admin/teams",
            meta: { page_reference: "teams", page_scope_label: "Teams" },
            name: "Teams",
            component: Teams
          },
          {
            path: "/admin/teams/new-team",
            meta: { page_reference: "new_team", page_scope_label: "New Team" },
            name: "NewTeam",
            component: NewTeam
          },
          {
            path: "/admin/integrations",
            meta: { page_reference: "integrations", page_scope_label: "Integrations" },
            name: "IntegrationsList",
            component: IntegrationsList
          },
          {
            path: "/admin/integrations/:integration_name/:integration_reference/:step?",
            meta: { page_reference: "rs_integration", page_scope_label: "Integration" },
            props: true,
            name: "IntegrationsMainContainer",
            component: IntegrationsMainContainer,
            beforeEnter: (to, _from, next) => {
              if (!to.params.integration_reference) {
                return next("/admin/integrations")
              }
              return next()
            }
          }
        ]
      },
      {
        path: "/workspace",
        component: WorkSpaceMain,
        name: "WorkSpaceMain",
        children: [
          {
            path: "/workspace/",
            name: "WorkspacesList",
            meta: { page_reference: "workspace_list", page_scope_label: "Workspaces" },
            component: WorkspacesList
          },
          {
            path: "/workspace/:wid/:page?",
            name: "WorkspaceItem",
            meta: { page_reference: "workspace_item", page_scope_label: "Workspace" },
            component: WorkspaceItem
          },
          {
            path: "/workspace/edit/:wid",
            name: "WorkspaceForm",
            meta: { page_reference: "workspace_edit", page_scope_label: "Edit Workspace" },
            component: WorkspaceForm
          }
        ]
      },
      {
        path: "/customer-inbox/",
        meta: { page_reference: "customer_inbox", page_scope_label: "Inbox" },
        name: "CustomerInbox",
        component: CustomerInbox
      },
      {
        path: "/homepage/:page?",
        meta: { page_reference: "homepage", page_scope_label: "Homepage" },
        name: "HomePage",
        component: HomePage,
        beforeEnter: (to, _from) => {
          if (!to.params.page) {
            if (store.state.portfolio?.pages_layout?.homepage?.filter((p) => !p.is_hidden && p.slug == "rs-onboarding")?.length > 0) {
              return to.path + (to.path[to.path.length - 1] == "/" ? "" : "/") + "rs-onboarding"
            } else {
              return to.path + (to.path[to.path.length - 1] == "/" ? "" : "/") + "home"
            }
          }
        }
      },
      {
        path: "/prospecting/:page?",
        meta: { page_reference: "prospecting", page_scope_label: "Prospecting" },
        name: "Prospecting",
        component: Prospecting,
        beforeEnter: (to, _from) => {
          if (!to.params.page) {
            return to.path + (to.path[to.path.length - 1] == "/" ? "" : "/") + "find_leads"
          }
        }
      },
      {
        path: "/dashboard/:page?",
        meta: { page_reference: "dashboard", page_scope_label: "Dashboard" },
        name: "Dashboard",
        component: Dashboard,
        beforeEnter: (to, _from) => {
          if (!to.params.page) {
            if (store.state.portfolio?.pages_layout?.dashboard?.filter((p) => !p.is_hidden)?.length > 0) {
              return "/dashboard/" + store.state.portfolio.pages_layout["dashboard"][0].id
            }
            if (store.state.portfolio.shared_layouts?.filter((l) => l.page == "dashboard").length) {
              return "/dashboard/" + store.state.portfolio.shared_layouts.filter((l) => l.page == "dashboard")[0].id
            }
          }
        }
      },
      {
        path: "/portfolio/:object_type/:cid/:page?",
        meta: { page_reference: "company_profile", page_scope_label: "Company Profile" },
        name: "CompanyProfile",
        component: CompanyProfile,
        beforeEnter: (to, _from) => {
          if (!to.params.page) {
            return to.path + (to.path[to.path.length - 1] == "/" ? `overview` : `/overview`)
          }
        }
      },
      {
        path: "/portfolio/:parent_object_type/:pid/:object_type/:cid/:page?",
        meta: { page_reference: "object_page", page_scope_label: "Profile" },
        name: "ObjectProfile",
        component: ObjectPageProfile,
        beforeEnter: (to, _from) => {
          if (!to.params.page) {
            // take to the first page overview
            return to.path + (to.path[to.path.length - 1] == "/" ? `overview` : `/overview`)
          }
        }
      },
      {
        path: "/plans/:pid",
        name: "PlanPage",
        meta: { page_reference: "plan", page_scope_label: "Plan" },
        component: PlanPageContainer
      },
      {
        path: "/revenue/forecast/:page?",
        meta: { page_reference: "forecasting", page_scope_label: "Forecasting" },
        name: "Forecasting",
        component: ForecastsContainer,
        beforeEnter: (to, _from) => {
          if (!to.params.page && store.state.portfolio.pages_layout?.forecasting?.filter((p) => !p.is_hidden).length) {
            let forecasting_pages_ids = store.state.data.user_forecasts?.map((p) => p.id)
            if (forecasting_pages_ids?.length > 0) {
              return to.path + (to.path[to.path.length - 1] == "/" ? "" : "/") + forecasting_pages_ids[0]
            }
          }
        }
      },
      {
        path: "/playbooks",
        name: "Playbooks",
        component: Playbooks,
        meta: { page_scope_label: "Playbooks" }
      },

      {
        path: "/surveys",
        name: "Surveys",
        component: Surveys,
        meta: { page_scope_label: "Surveys" }
      },
      {
        path: "/playbooks/new",
        name: "NewPlaybook",
        component: PlaybookEditor,
        props: { isNewPlaybook: true },
        meta: { page_scope_label: "New Playbook" }
      },
      {
        path: "/playbooks/:id",
        name: "PlaybookEditor",
        component: PlaybookEditor,
        meta: { page_scope_label: "Playbook" }
      },
      {
        path: "/playbooks/:id/executions",
        name: "PlaybookExecutions",
        component: PlaybookExecutions,
        meta: { page_scope_label: "Playbook Executions" }
      },
      {
        path: "/expansion/Journeys",
        meta: { page_reference: "customer_journeys", page_scope_label: "Customer Journeys" },
        name: "Customer Journeys",
        component: Journeys
      },
      {
        path: "/portfolio/:page?",
        meta: { page_reference: "portfolio", page_scope_label: "Portfolio" },
        name: "Portfolio",
        component: Portfolio,
        props: true,
        beforeEnter: (to, _from) => {
          return beforeEnterfunc()

          function beforeEnterfunc() {
            const portfolio_pages = store.state?.portfolio.pages_layout?.portfolio
            if (!portfolio_pages) {
              setTimeout(() => {
                beforeEnterfunc()
              }, 500)
            }
            if (!to.params.page && portfolio_pages?.length && portfolio_pages.filter((p) => !p.is_hidden).length) {
              const first_page_slug = portfolio_pages.filter((p) => !p.is_hidden)[0].slug
              const first_page_id = portfolio_pages.filter((p) => !p.is_hidden)[0].id
              let first_page_id_or_slug = first_page_id
              if (store.state.object_types.map((o) => o.type).includes(first_page_slug)) {
                first_page_id_or_slug = first_page_slug
              }
              router.replace(`${to.path}${to.path[to.path.length - 1] == "/" ? "" : "/"}${first_page_id_or_slug}`)
            }
          }
        }
      },

      {
        path: "/admin-page",
        name: "AdminPage",
        meta: { page_reference: "admin_page", page_scope_label: "Admin Page" },
        component: AdminPage,
        redirect: "/admin-page/suggested-lists",
        children: [
          {
            path: "/admin-page/api-keys",
            name: "ApiKeys",
            component: ApiKeys,
            meta: { page_scope_label: "API Keys" }
          },
          {
            path: "/admin-page/suggested-lists",
            name: "SuggestedLists",
            component: SuggestedLists,
            meta: { page_scope_label: "Suggested List" }
          },
          {
            path: "/admin-page/templates",
            name: "Templates",
            component: Templates,
            meta: { page_scope_label: "Templates" }
          },
          {
            path: "/admin-page/growth-matrices",
            name: "GrowthMatrices",
            component: GrowthMatrices,
            meta: { page_scope_label: "Growth Matrices" }
          },
          {
            path: "/admin-page/forecasting-preferences",
            name: "ForecastingPreferences",
            component: ForecastingPreferences,
            meta: { page_scope_label: "Forecasting Preferences" }
          },
          {
            path: "/admin-page/global-variables",
            name: "GlobalVariables",
            component: GlobalVariables,
            meta: { page_scope_label: "Global Variables" }
          }
        ]
      },
      {
        path: "/settings",
        name: "Settings",
        meta: { page_scope_label: "Settings" },
        component: Settings,
        redirect: "/settings/profile",
        children: [
          {
            path: "profile",
            name: "Profile",
            component: Profile,
            meta: { page_scope_label: "Profile" }
          },
          {
            path: "aboutme",
            name: "More about me",
            component: AboutMe,
            meta: { page_scope_label: "More about me" }
          },
          {
            path: "organization",
            name: "Organization",
            component: Organization,
            meta: { page_scope_label: "Organization" }
          },
          {
            path: "password",
            name: "PasswordReset",
            component: PasswordReset,
            meta: { page_scope_label: "Password" }
          },
          {
            path: "themes",
            name: "Themes",
            component: Themes,
            meta: { page_scope_label: "Themes" }
          },
          {
            path: "my-activities",
            name: "My Activities",
            component: MyActivities,
            meta: { page_scope_label: "MyActivities" }
          },
          {
            path: "billing",
            name: "Billing",
            component: Billing,
            meta: { page_scope_label: "Plans & Billing" }
          }
        ]
      }
    ]
  },
  // redirect plg-onboarding to onboarding
  {
    path: "/plg-onboarding",
    redirect: "/onboarding"
  },
  {
    path: "/start",
    redirect: "/onboarding"
  },
  {
    path: "/onboarding",
    component: OnboardingContainer,
    name: "OnBoarding",
    meta: { page_reference: "onboarding", page_scope_label: "Onboarding" }
  },
  {
    path: "/login",
    meta: { page_reference: "login", page_scope_label: "Login" },
    name: "Login",
    component: Login
  },
  {
    path: "/login",
    meta: { page_reference: "login", page_scope_label: "Login" },
    name: "Login",
    component: Login
  },
  {
    path: "/workspace-login",
    name: "WorkSpaceLogin",
    meta: { page_reference: "end_user_login", page_scope_label: "Workspace Login" },
    component: WorkSpaceLogin
  },
  {
    path: "/share-space-login",
    name: "ShareSpaceLogin",
    meta: { page_reference: "end_user_login", page_scope_label: "Sharespace Login" },
    component: ShareSpaceLogin
  },

  {
    path: "/signup",
    meta: { page_reference: "signup", page_scope_label: "Signup" },
    name: "Signup",
    component: Signup,
    // we add guard that shows input of Invite code if the user is not invited
    // if the code is correct we will redirect to the same page
    beforeEnter: async (to, _from) => {
      if (to.query.invite_code) {
        let res = await authService.checkInviteCode({ invite_code: to.query.invite_code }).catch(() => {
          return "/protected-resource"
        })
        if (res?.data?.status == "VALID_INVITE_CODE") {
          return true
        }
        return "/protected-resource"
      }
      return "/protected-resource"
    }
  },
  {
    path: "/privacy-policy",
    name: "Privacy Policy",
    meta: { page_reference: "privacy_policy", page_scope_label: "Privacy Policy" },
    component: PrivacyPolicy
  },
  {
    path: "/terms-and-conditions",
    name: "Terms and Conditions",
    meta: { page_reference: "terms_and_conditions", page_scope_label: "Terms and  Conditions" },
    component: TermsAndConditions
  },
  {
    path: "/cookies-policy",
    name: "Cookies Policy",
    meta: { page_reference: "signup", page_scope_label: "Cookies Policy" },
    component: CookiesPolicy
  },
  {
    path: "/integrations/:integration_name/callback",
    name: "ServiceCallback",
    meta: { page_reference: "signup", page_scope_label: "Service Callback" },
    component: ServiceCallback
  },

  {
    path: "/recover-password",
    name: "RecoverPassword",
    meta: { page_reference: "recover_password", page_scope_label: "Recover password" },
    component: RecoverPassword
  },
  {
    path: "/reset-password/:uid/:token",
    name: "ResetPassword",
    meta: { page_reference: "reset_password", page_scope_label: "Reset password" },
    component: ResetPassword
  },
  {
    path: "/join-team/:email/:uid/:token",
    name: "JoinTeam",
    meta: { page_reference: "join_team", page_scope_label: "Join Team" },
    component: JoinTeam
  },
  {
    path: "/survey/:org_id/:record_id/:score_id",
    name: "Survey",
    meta: { page_reference: "Survey", page_scope_label: "Survey" },
    component: ExternalSurvey
  },
  {
    path: "/end-user/join/:org/:email/:uid/:token",
    name: "ShareSpaceJoin",
    meta: { page_reference: "end_user_join", page_scope_label: "Join Sharespace" },
    component: ShareSpaceJoin
  },
  {
    path: "/workspace/join/:workspace_id/:email/:uid/:token",
    name: "WorkSpaceJoin",
    meta: { page_reference: "end_user_join", page_scope_label: "Join Workspace" },
    component: WorkSpaceJoin
  },

  {
    path: "/share-space/",
    component: ShareSpaceMain,
    name: "ShareSpaceMain",
    meta: { page_reference: "share_space", page_scope_label: "Customer Share Space" },
    children: [
      {
        path: "/share-space/:org",
        name: "ShareSpaceHome",
        meta: { page_reference: "share_space_home", page_scope_label: "Customer Share Space" },
        component: ShareSpaceHome
      },
      {
        path: "/share-space/:org/:object_type",
        name: "ShareSpaceObjectsList",
        meta: { page_reference: "share_space_objects", page_scope_label: "Customer Share Space" },
        component: ShareSpaceObjects
      },
      {
        path: "/share-space/:org/:object_type/:object_id",
        name: "ShareSpaceObject",
        meta: { page_reference: "share_space_object", page_scope_label: "Customer Share Space" },
        component: ShareSpaceObject
      }
    ]
  },

  {
    path: "/logout",
    name: "Logout",
    template: "",
    component: {
      mounted() {
        store.dispatch("auth/logout", this.$router)
      }
    }
  },
  {
    path: "/magic/login",
    name: "MagicLogin",
    component: {
      created() {
        window.localStorage.clear()
        store
          .dispatch("auth/magic_login", this.$route.query.token)
          .then(() => {
            this.$router.replace("/")
          })
          .catch(() => {
            this.$router.replace("/")
          })
      }
    }
  },
  {
    path: "/session/login",
    name: "SessionLogin",
    component: {
      created() {
        window.localStorage.clear()
        store
          .dispatch("auth/sessionLogin", this.$route.query.token)
          .then(() => {
            this.$router.replace("/")
          })
          .catch(() => {
            this.$router.replace("/")
          })
      }
    }
  },
  {
    path: "/denied-access",
    name: "PermissionRequiredPage",
    meta: { page_reference: "denied", page_scope_label: "Access Denied" },
    component: PermissionRequiredPage
  },
  {
    path: "/protected-resource",
    name: "ProtectedResourcePage",
    meta: { page_reference: "protected", page_scope_label: "Protected Resource" },
    component: ProtectedResourcePage
  },
  {
    path: "/:pathMatch(.*)*",
    name: "PageNotFound",
    component: PageNotFound,
    meta: {
      page_reference: "404",
      page_scope_label: "Page Not Found",
      statusCode: 404
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.BASE_URL),
  routes
})
// Routes
// auth and none auth users
const all_users_open_routes = [
  "Privacy Policy",
  "Terms and Conditions",
  "OnBoarding",
  "Cookies Policy",
  "MagicLogin",
  "SessionLogin",
  "ExternalSurvey",
  "Survey",
  "Logout",
  "ProtectedResourcePage"
]
const anonymous_users_open_routes = [
  "Login",
  "Signup",
  "JoinTeam",
  "ShareSpaceLogin",
  "ExternalSurvey",
  "ShareSpaceJoin",
  "WorkSpaceLogin",
  "WorkSpaceJoin",
  "RecoverPassword",
  "ResetPassword"
]

const end_users_open_routes = [
  "ShareSpaceMain",
  "WorkspacesList",
  "WorkspaceItem",
  "WorkspaceForm",
  "ShareSpaceHome",
  "ShareSpaceObjectsList",
  "ShareSpaceObject",
  "Logout"
]
// const workspace_end_users_open_routes = ["WorkSpaceMain", "WorkSpaceHome", "WorkSpaceObjectsList", "WorkSpaceObject", "Logout"]
const users_open_routes = ["Login", "Logout", "WorkspacesList", "WorkspaceItem", "WorkspaceForm"]

const links = [
  { href: "/homepage/home", event_name: "home_page_click" },
  { href: "/homepage/home_upcoming_renewals", event_name: "home_upcoming_renewals_click" },
  { href: "/homepage/home_today", event_name: "home_today_click" },
  { href: "/homepage/home_this_week", event_name: "home_this_week_click" },
  { href: "/homepage/home_this_month", event_name: "home_this_month_click" },
  { href: "/homepage/home_overdue", event_name: "home_overdue_click" },
  { href: "/portfolio/company", event_name: "portfolio_company_click" },
  { href: "/portfolio/opportunity", event_name: "portfolio_opportunity_click" },
  { href: "/portfolio/contact", event_name: "portfolio_contact_click" },
  { href: "/dashboard/", event_name: "dashboard_click" },
  { href: "/admin-page", event_name: "admin_tools_click" },
  { href: "/admin/teams", event_name: "teams_click" },
  { href: "/admin/integrations", event_name: "integrations_click" },
  { href: "/admin/metrics", event_name: "metrics_click" },
  { href: "/workspace", event_name: "workspace_click" },
  { href: "/playbooks", event_name: "playbooks_click" },
  { href: "/surveys", event_name: "surveys_click" },
  { href: "/revenue/forecast/", event_name: "forecast_click" },
  { href: "/prospecting/suggested_list", event_name: "suggested_list_click" },
  { href: "/prospecting/growth_matrix", event_name: "growth_matrix_click" },
  { href: "/prospecting/expansion_lists", event_name: "expansion_lists_click" },
  { href: "/prospecting/find_leads", event_name: "find_leads_click" },
  { href: "/customer-inbox", event_name: "customer_inbox_click" },
  { href: "/settings", event_name: "settings_click" }
]
// router.removeRoute('about')
router.beforeEach(async (to, from, next) => {
  document.title = "RevSetter | " + (to.meta?.page_scope_label ? to.meta.page_scope_label : to.name)

  // Set HTTP status code for 404 pages
  if (to.meta?.statusCode === 404) {
    document.documentElement.style.setProperty("--http-status", "404")
  } else {
    document.documentElement.style.removeProperty("--http-status")
  }

  if (all_users_open_routes.includes(to.name)) {
    return next()
  }

  let isLoggedIn = false
  try {
    isLoggedIn = store.state.auth.status.isLoggedIn && store.state.auth.user.user_type
  } catch (error) {
    isLoggedIn = false
  }
  if (!isLoggedIn && anonymous_users_open_routes.includes(to.name)) {
    if (to.name.includes("Onboarding")) {
      return next({ name: "Onboarding", query: { link: to.path } })
    }

    return next()
  }
  if (!isLoggedIn) {
    if (end_users_open_routes.includes(to.name)) {
      document.title = "RevSetter | " + to.meta.page_scope_label
      if (to.name.includes("Sharespace")) {
        return next({ name: "ShareSpaceLogin", query: { link: to.path } })
      } else if (to.name.includes("Workspace")) {
        return next({ name: "WorkSpaceLogin", query: { link: to.path } })
      } else if (to.name.includes("Onboarding")) {
        return next({ name: "Onboarding", query: { link: to.path } })
      } else {
        // return next({ name: "Login", query: { link: to.path } })
      }
    } else {
      if (to.name.includes("Onboarding")) {
        return next({ name: "Onboarding", query: { link: to.path } })
      } else {
        return next({ name: "Login", query: { link: to.path } })
      }
    }
  }

  // from here only Auth users

  const isEndUser = store.state.auth.user?.user_type == "end_user"
  // if to login allow
  if (to.name == "Login") {
    return next()
  }
  if (anonymous_users_open_routes.includes(to.name)) {
    if (isEndUser) {
      document.title = "RevSetter | " + to.meta.page_scope_label
      return next({ name: "WorkspacesList" })
    } else {
      return next({ name: "Home" })
    }
  }

  if (isEndUser) {
    // first time access

    document.title = "RevSetter | " + to.meta.page_scope_label
    if (end_users_open_routes.includes(to.name)) {
      return next()
    } else {
      return next({ name: "WorkspacesList" })
    }
  } else {
    // Normal platform Users
    if (users_open_routes.includes(to.name)) {
      return next()
    }

    // for normal users denied access to end users pages
    if (end_users_open_routes.includes(to.name)) {
      return next({ name: "PermissionRequiredPage", query: { scope: to.meta?.page_scope_label } })
    }
    // only if the control is not fetched we fetch it again
    // first time access

    const isEnterpriseEdition = store.state.control.is_enterprise_edition
    // if not finished onboarding keep on that page

    if (!store.state.auth.user?.is_onboarding_done && to?.name?.toLowerCase().includes("onboarding")) {
      return next()
    }
    if (!store.state.auth.user?.is_onboarding_done && to.meta?.page_reference != "onboarding") {
      await store.dispatch("auth/refreshMe").catch(async (err) => {
        if (err.response?.status == 401 && store.state.auth?.token) {
          await store.dispatch("auth/logout")
        }
      })
      if (!store.state.auth.user?.is_onboarding_done) {
        return next({ path: "/onboarding/" })
      }
    } else {
      router.hasRoute("OnBoarding") ? router.removeRoute("OnBoarding") : null
    }

    if (to.name == "Home") {
      if (isEnterpriseEdition) {
        return next({ name: "HomePage" })
      } else {
        return next({ name: "Portfolio" })
      }
    } else {
      return next()
    }
  }
})

if (import.meta.env.VITE_NODE_ENV === "production" && window.hj) {
  router.afterEach((to) => {
    window.hj("identify", store?.state?.auth?.user?.email || null, {
      env: import.meta.env.VITE_NODE_ENV,
      user_id: store?.state?.auth?.user?.id,
      user_email: store?.state?.auth?.user?.email
    })
    try {
      let dashboard_page = store.state.portfolio.pages_layout["dashboard"].find((page) => page.id == to.params.page)
      if (dashboard_page) {
        google_event("dashboard_item_click", {
          dashboard_name: dashboard_page.name,
          page: dashboard_page.page,
          user_name: this.$store.state.auth.user.full_name,
          user_email: this.$store.state.auth.user.email,
          organization: this.$store.state.organization.name
        })
      } else {
        let event = links.find((l) => l.href == to.href)
        google_event(event.event_name, {
          user_name: this.$store.state.auth.user.full_name,
          user_email: this.$store.state.auth.user.email,
          organization: this.$store.state.organization.name
        })
      }
    } catch (error) {
      //console.log(error)
    }
  })
}

export default router
